import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NotificationsService } from 'api/notifications.api';
import {
    GetStationSettingsResponse,
    PatchStationSettingsRequest,
    PatchStationSettingsResponse,
    PutStationSettingsRequest,
    PutStationSettingsResponse,
    RevertStationSettingsRequest,
    RevertStationSettingsResponse,
    SettingsApiService,
} from '@kempoweroyj/ngx-settings-api-client';
import { AuthService } from 'providers/auth.service';
import { merge, Subject } from 'rxjs';
import { from } from 'rxjs/internal/observable/from';
import { filter, startWith, switchMap } from 'rxjs/operators';
import type { BaseConfiguration } from '@kempowersw/charger-config';

export interface StationSettingsBase {
    controllingSystem?: 'cloud' | 'device';
    configuration: BaseConfiguration;
}

export type StationSettingsBaseWithProfile = StationSettingsBase &
    Pick<GetStationSettingsResponse['settings'], 'settingsProfile' | 'externalCloudConfiguration'>;
export interface SettingsProfileSaveEvent {
    settings: StationSettingsBaseWithProfile;
    requestSync?: boolean;
}

@Injectable({ providedIn: 'root' })
export class SettingsService extends SettingsApiService {
    constructor(
        http: HttpClient,
        authService: AuthService,
        private notifications: NotificationsService
    ) {
        super(http, authService);
    }

    async putStationSettings(stationId: string, body: PutStationSettingsRequest): Promise<PutStationSettingsResponse> {
        if (body?.settings) {
            body.settings.settingsSource = 'cloud';
        }
        return super.putStationSettings(stationId, body).finally(() => {
            this.notifySettingsShouldBeRefreshed(stationId);
        });
    }

    async patchStationSettings(
        stationId: string,
        body: PatchStationSettingsRequest
    ): Promise<PatchStationSettingsResponse> {
        if (body?.settings) {
            body.settings.settingsSource = 'cloud';
        }
        return super.patchStationSettings(stationId, body).finally(() => {
            this.notifySettingsShouldBeRefreshed(stationId);
        });
    }
    async revertStationSettings(
        stationId: string,
        body: RevertStationSettingsRequest
    ): Promise<RevertStationSettingsResponse> {
        return super.revertStationSettings(stationId, body).finally(() => {
            this.notifySettingsShouldBeRefreshed(stationId);
        });
    }

    settingsProfile$(profileId: string) {
        return from(this.getSettingsProfile(profileId));
    }

    private _settingsShouldBeRefreshed$ = new Subject<string>();
    notifySettingsShouldBeRefreshed(stationId: string) {
        this._settingsShouldBeRefreshed$.next(stationId);
    }
    settingsShouldBeRefreshed$(stationId: string) {
        const settingsUpdated$ = this.notifications.notification$.pipe(
            filter(
                (n) =>
                    n.type === 'ChargingStationUpdated' &&
                    n.stationId === stationId &&
                    (n.changedSchemas.includes('settings') || n.changedSchemas.includes('externalNotificationSettings'))
            )
        );
        const up$ = merge(
            this._settingsShouldBeRefreshed$.pipe(filter((_stationId) => _stationId === stationId)),
            settingsUpdated$
        );
        return up$;
    }

    private _externalNotificationSettingsShouldBeRefreshed$ = new Subject<void>();
    notifyExternalNotificationSettingsShouldBeRefreshed() {
        this._externalNotificationSettingsShouldBeRefreshed$.next();
    }
    externalNotificationSettingsShouldBeRefreshed$() {
        const settingsUpdated$ = this.notifications.notification$.pipe(
            filter(
                (n) =>
                    n.type === 'ChargingStationUpdated' &&
                    !n.stationId &&
                    !n.locationUid &&
                    n.changedSchemas.includes('externalNotificationSettings')
            )
        );
        const up$ = merge(this._externalNotificationSettingsShouldBeRefreshed$, settingsUpdated$);
        return up$;
    }

    externalNotificationSettings$() {
        return this.externalNotificationSettingsShouldBeRefreshed$().pipe(
            startWith(null),
            switchMap(() => from(this.getExternalNotificationSettings()))
        );
    }
}
